import { mapGetters, mapActions } from "vuex";
import * as types from "@/store/types";
import debounce from "lodash-es/debounce";
import find from "lodash-es/find";
import { OrderService } from "@/services";
import $rate from "@/mixins/rate";
import $exponent from "@/mixins/exponent";
import $thousandMask from "@/mixins/thousandMask";
import truncate from "@/filters/truncate";
import marketService from "@/services/modules/marketService";

const RangeSlider = () =>
  import(
    /* webpackChunkName: "RangeSlider" */ "@/components/OrderPage/RangeSlider/index"
  );

const OrderInvoiceCard = () =>
  import(
    /* webpackChunkName: "OrderInvoiceCard" */ "@/components/OrderPage/OrderInvoiceCard/index"
  );

const OrderNotificationCard = () =>
  import(
    /* webpackChunkName: "OrderInvoiceCard" */ "@/components/OrderPage/OrderNotificationCard/index"
  );

export default {
  name: "order-form",
  components: {
    OrderInvoiceCard,
    OrderNotificationCard,
    RangeSlider
  },
  mixins: [$rate, $exponent, $thousandMask],
  filters: { truncate },

  data() {
    return {
      marketsTime: [],
      amount: "",
      amountRules: "",

      price: "",
      priceRules: "",

      order: "",
      orderRules: "",

      orderPercent: "",
      orderPercentRules: "",

      orderHour: "",
      orderMinute: "",
      timeRules: "",

      rate: 1,
      sliderValue: "0",
      payableAmount: 2000,
      showOrderLimit: true,
      isOrderPercent: 0,
      dialog: false,
      notificationDialog: false,
      resultData: {},
      lastDesCurrency: "",
      timeValidation: true,
      oldTab: "",
      valueStatus: false,
      valid: false,
      loading: false,
      publicUrl: process.env.VUE_APP_PUBLIC_BASE_URL
    };
  },

  mounted() {
    this.updateMaxTtl();
    this.resetRate();
    this.getMarketTime();
  },

  computed: {
    ...mapGetters({
      baseId: types.market.getters.GET_SELECTED_BASE_CURRENCY,
      asset: types.market.getters.GET_TO_CURRENCY,
      defaultAsset: types.market.getters.GET_DEFAULT_TO_CURRENCY,
      currencies: types.currencies.getters.GET_CURRENCIES,
      markets: types.market.getters.GET_AVAILABLE_MARKET_DATA,
      wallets: types.wallets.getters.GET_WALLETS,
      buySellState: types.buySellState.getters.GET_BUY_SELL_STATE
    }),

    desCurrency() {
      let des = this.asset;
      if (Object.entries(des).length === 0) {
        des = this.defaultAsset;
      }
      return des;
    },

    isFormValid() {
      const isInputsFill = this.amount && this.price && this.order;
      const isInputsValid = !(
        this.priceRules ||
        this.amountRules ||
        this.orderRules ||
        this.orderPercentRules ||
        this.timeRules
      );

      return isInputsFill && isInputsValid;
    },

    maxHour() {
      return parseInt(this.market.max_ttl_millisecond / 3.6e6);
    },

    maxMinute() {
      return parseInt((this.market.max_ttl_millisecond % 3.6e6) / 60000);
    },

    balance() {
      if (this.wallets) {
        if (
          Object.entries(this.wallets.wallets).length !== 0 &&
          this.buySellState
        ) {
          if (this.buySellState === "buy") {
            const balance = this.wallets.wallets.find(
              wallet => wallet.currency.id === this.base.id
            ).balance;
            return this.$exponent(balance, this.baseExponent);
          } else {
            const balance = this.wallets.wallets.find(
              wallet => wallet.currency.id === this.desCurrency.id
            ).balance;
            return this.$exponent(balance, this.assetExponent);
          }
        }
      }
    },

    fee() {
      if (this.buySellState === "buy") {
        const result =
          parseFloat(this.pureAmount) *
          (parseFloat(this.market.change_fee_buy) / 100);
        const expo = this.$exponent(result, this.assetExponent);
        return result ? expo : "0";
      } else if (this.buySellState === "sell") {
        const result =
          parseFloat(this.purePrice) *
          (parseFloat(this.market.change_fee_sell) / 100);
        const expo = this.$exponent(result, this.baseExponent);
        return result ? expo : "0";
      }
    },

    // totalOutcome() {
    //   let result;
    //   if (this.buySellState === "buy") {
    //     result = this.$exponent(this.purePrice, this.base);
    //   } else {
    //     result = this.$exponent(this.pureAmount, this.desCurrency);
    //   }
    //   return result ? result : "0";
    // },

    totalIncome() {
      let result;
      if (this.buySellState === "buy") {
        const value = parseFloat(this.pureAmount) - parseFloat(this.fee);
        result = value && this.$exponent(value, this.assetExponent);
      } else if (this.buySellState === "sell") {
        const value = parseFloat(this.purePrice) - parseFloat(this.fee);
        result = value && this.$exponent(value, this.baseExponent);
      }

      return result ? result : "0";
    },

    market() {
      if (Object.entries(this.desCurrency).length !== 0) {
        return find(this.markets, {
          base_currency_id: this.baseId,
          asset_currency_id: this.desCurrency.id
        });
      }
    },

    baseExponent() {
      return this.market?.base_computational_exponent;
    },

    assetExponent() {
      return this.market?.asset_computational_exponent;
    },

    base() {
      return this.currencies?.find(currency => currency.id === this.baseId);
    },

    purePrice() {
      return this.removeThousand(this.price);
    },

    pureAmount() {
      return this.removeThousand(this.amount);
    },

    assets() {
      return this.markets.filter(
        market => market.base_currency_id === this.baseId
      );
    },

    originalRate() {
      return this.$rate(this.base, this.desCurrency, this.buySellState);
    },

    pureOrder() {
      return this.removeThousand(this.order);
    },

    isTimeEmpty() {
      const { orderMinute, orderHour } = this;

      return (
        orderHour === "" ||
        orderMinute === "" ||
        orderMinute === "0" ||
        orderMinute === "00"
      );
    },

    isTimeValid() {
      const hour = this.orderHour * 3.6e6;
      const minute = this.orderMinute * 6e4;
      const inputMaxTtl = hour + minute;
      return inputMaxTtl <= this.market.max_ttl_millisecond;
    },

    orderPercentMaxLength() {
      let orderPercentString = this.orderPercent.toString();
      if (orderPercentString.includes(".")) {
        if (this.orderPercent < 0) {
          return 6;
        } else if (this.orderPercent > 0) {
          return 5;
        }
      } else {
        if (this.orderPercent < 0) {
          return 4;
        } else if (this.orderPercent > 0) {
          return 3;
        }
      }
    }
  },

  methods: {
    ...mapActions({
      setAsset: types.market.actions.SET_TO_CURRENCY
    }),
    getMarketTime() {
      marketService.getMarketTimes().then(res => {
        this.marketsTime = res.data.data;
      });
    },

    resetRate() {
      this.rate = this.originalRate;
    },

    getOrderTime(hour, minute) {
      const hourMs = hour * 3.6e6;
      const minuteMs = minute * 60000;
      return hourMs + minuteMs;
    },

    setSlider(percent) {
      this.sliderValue = percent;
      if (this.buySellState === "buy") {
        /* set price */
        const priceResult = (percent / 100) * this.balance;
        const str = priceResult.toString();
        const zeroChecked = this.leadingZero(str);
        const priceExpo = this.$exponent(zeroChecked, this.baseExponent);
        const noComma = this.removeThousand(priceExpo);
        const mask = this.$thousandMask(noComma);
        this.price = noComma ? mask : "";
        /* set amount */
        const amountResult = this.purePrice / this.rate;
        const amountExpo = this.$exponent(amountResult, this.assetExponent);
        const amountMask = this.$thousandMask(amountExpo);
        this.amount = amountResult ? amountMask : "";
        this.checkAmountValidation();
      } else if (this.buySellState === "sell") {
        /* set amount */
        const amountResult = (percent / 100) * this.balance;
        const str = amountResult.toString();
        const zeroChecked = this.leadingZero(str);
        const expo = this.$exponent(zeroChecked, this.assetExponent);
        const noComma = this.removeThousand(expo);
        const amountMask = this.$thousandMask(noComma);
        this.amount = noComma ? amountMask : "";

        /* set price */
        const fromResult = this.pureAmount * this.rate;
        const fromExpo = this.$exponent(fromResult, this.baseExponent);
        const mask = this.$thousandMask(fromExpo);
        this.price = fromExpo ? mask : "";
        this.checkPriceValidation();
      }
    },

    updateSlider() {
      let result;
      if (this.buySellState === "buy") {
        const price = parseFloat(this.purePrice);
        result = (price * 100) / this.balance;
      } else if (this.buySellState === "sell") {
        const amount = parseFloat(this.pureAmount);
        result = (amount * 100) / this.balance;
      }
      if (result > 100) {
        this.sliderValue = "100";
      } else if (result < 0) {
        this.sliderValue = "0";
      } else if (!isNaN(result)) {
        this.sliderValue = this.$exponent(result);
      } else if (this.price === "") {
        this.sliderValue = "0";
      }
    },

    leadingZero(value) {
      let number = value.toString();
      if (value.includes(".")) {
        while (number.charAt(0) === "0" && number.charAt(1) !== ".") {
          number = number.substring(1);
        }
      } else if (number.slice(-1) !== "0") {
        while (number.charAt(0) === "0") {
          number = number.substring(1);
        }
      }
      return number;
    },

    removeThousand(value) {
      const str = value.toString();
      return str?.replace(/,/gi, "");
    },

    setPrice({ target: { value } }) {
      const set = price => {
        const enDigits = this.toEnglishDigits(price);
        const noComma = this.removeThousand(enDigits);
        const zeroLead = this.leadingZero(noComma);
        const priceMask = this.$thousandMask(zeroLead);
        this.price = noComma ? priceMask : "";

        /* set amount */
        const amountResult = this.purePrice / this.rate;
        if (!isNaN(amountResult)) {
          const amountExpo = this.$exponent(amountResult, this.assetExponent);
          const amountMask = this.$thousandMask(amountExpo);
          this.amount = amountResult ? amountMask : "";

          this.updateSlider();
        }
      };

      if (value.includes(".")) {
        const spl = value.split(".");
        const decimal = spl[1];
        if (decimal.length > this.baseExponent) {
          const noComma = this.removeThousand(value);
          const expo = this.$exponent(noComma, this.baseExponent);
          this.price = this.$thousandMask(expo);
        } else {
          set(value);
        }
      } else {
        set(value);
      }
      this.resetRangeSlider();

      /* validation */
      this.buySellState === "buy"
        ? this.checkAmountValidation()
        : this.checkPriceValidation();
    },

    updatePrice() {
      if (this.amount) {
        const priceResult = this.pureAmount * this.rate;
        const priceExpo = this.$exponent(priceResult, this.baseExponent);
        const priceMask = this.$thousandMask(priceExpo);
        this.price = priceResult ? priceMask : "";
      }
    },

    setAmount({ target: { value } }) {
      const set = amount => {
        const enDigits = this.toEnglishDigits(amount);
        const noComma = this.removeThousand(enDigits);
        const zeroLead = this.leadingZero(noComma);
        const amountMask = this.$thousandMask(zeroLead);
        this.amount = noComma ? amountMask : "";

        /* set price */
        const priceResult = this.pureAmount * this.rate;
        if (!isNaN(priceResult)) {
          const priceExpo = this.$exponent(priceResult, this.baseExponent);
          const priceMask = this.$thousandMask(priceExpo);
          this.price = priceResult ? priceMask : "";

          this.updateSlider();
        }
      };

      if (value.includes(".")) {
        const spl = value.split(".");
        const decimal = spl[1];
        if (decimal.length > this.assetExponent) {
          const noComma = this.removeThousand(value);
          const expo = this.$exponent(noComma, this.assetExponent);
          this.amount = this.$thousandMask(expo);
        } else {
          set(value);
        }
      } else {
        set(value);
      }
      this.resetRangeSlider();

      /* validation */
      this.buySellState === "buy"
        ? this.checkAmountValidation()
        : this.checkPriceValidation();
    },

    checkAmountValidation() {
      this.amountRules = "";
      const assetMax = parseFloat(this.market.maximum_amount_asset_currency);
      const assetMin = parseFloat(this.market.minimum_amount_asset_currency);
      const amount = parseFloat(this.pureAmount);

      if (amount && amount < assetMin) {
        this.amountRules = this.$t("orderPage.orderForm.minAmountRules", {
          assetMin: this.$thousandMask(assetMin),
          iso: this.desCurrency.iso
        });
      } else if (amount && amount > assetMax) {
        this.amountRules = this.$t("orderPage.orderForm.maxAmountRules", {
          assetMax: this.$thousandMask(assetMax),
          iso: this.desCurrency.iso
        });
      }
    },

    checkPriceValidation() {
      this.priceRules = "";
      const baseMax = parseFloat(this.market.maximum_amount_base_currency);
      const baseMin = parseFloat(this.market.minimum_amount_base_currency);
      const price = parseFloat(this.purePrice);

      if (price && price < baseMin) {
        this.priceRules = this.$t("orderPage.orderForm.minPriceRules", {
          baseMin: this.$thousandMask(baseMin),
          iso: this.base.iso
        });
      } else if (price && price > baseMax) {
        this.priceRules = this.$t("orderPage.orderForm.maxPriceRules", {
          baseMax: this.$thousandMask(baseMax),
          iso: this.base.iso
        });
      }
    },

    checkOrderValidation() {
      this.orderRules = "";
      const order = parseFloat(this.pureOrder);
      const range = (this.market.tolerance_percent / 100) * this.originalRate;
      let min, max;

      if (this.buySellState === "buy") {
        min = this.originalRate - range;
        max = this.originalRate;
      } else if (this.buySellState === "sell") {
        min = this.originalRate;
        max = this.originalRate + range;
      }

      const minExpo = this.$exponent(min, this.baseExponent);
      const minMask = this.$thousandMask(minExpo);
      const maxExpo = this.$exponent(max, this.baseExponent);
      const maxMask = this.$thousandMask(maxExpo);

      if (order < minExpo) {
        this.orderRules = this.$t("orderPage.orderForm.minOrderRules", {
          minMask,
          iso: this.base.iso
        });
      }

      if (order > maxExpo) {
        this.orderRules = this.$t("orderPage.orderForm.maxOrderRules", {
          maxMask,
          iso: this.base.iso
        });
      }
    },

    checkOrderPercentValidation() {
      this.orderPercentRules = "";
      const str = this.orderPercent.toString();
      const en = this.toEnglishDigits(str);
      const int = parseInt(en);

      if (this.buySellState === "buy") {
        if (int < -this.market.tolerance_percent) {
          this.orderPercentRules = this.$t(
            "orderPage.orderForm.minOrderPercentRules",
            {
              percent: this.market.tolerance_percent
            }
          );
        }

        if (int > 0) {
          this.orderPercentRules = this.$t(
            "orderPage.orderForm.maxZeroOrderPercentRules"
          );
        }
      } else if (this.buySellState === "sell") {
        if (int < 0) {
          this.orderPercentRules = this.$t(
            "orderPage.orderForm.minZeroOrderPercentRules"
          );
        }

        if (int > this.market.tolerance_percent) {
          this.orderPercentRules = this.$t(
            "orderPage.orderForm.maxOrderPercentRules",
            {
              percent: this.market.tolerance_percent
            }
          );
        }
      }
    },

    checkTimeValidation() {
      this.timeRules = "";

      if (!this.isTimeValid) {
        const minuteRule = this.$t("orderPage.orderForm.maxMinute", {
          maxMinute: this.maxMinute
        });

        const hourRule = this.maxHour
          ? this.$t("orderPage.orderForm.maxHour", {
              maxHour: this.maxHour
            })
          : "";

        const andRule =
          this.maxMinute && this.maxHour
            ? this.$t("orderPage.orderForm.and")
            : "";

        this.timeRules = this.$t("orderPage.orderForm.timeRules", {
          hourRule,
          andRule,
          minuteRule
        });
      } else if (this.isTimeEmpty) {
        this.timeRules = this.$t("orderPage.orderForm.specifyOrderTime");
      }
    },

    // setOrderHour(value) {
    //   const isOnlyTime = str => {
    //     const reg = new RegExp(/^\d*$/);
    //     return reg.test(str);
    //   };
    //
    //   if (isOnlyTime(value)) {
    //     this.orderHour = this.toEnglishDigits(value);
    //     this.checkTimeValidation();
    //   }
    // },

    resetOrderMinute() {
      this.orderMinute = "";
    },

    setOrderMinute({ target: { value } }) {
      const isOnlyTime = str => {
        const reg = new RegExp(/^\d*$/);
        return reg.test(str);
      };

      if (isOnlyTime(value)) {
        this.orderMinute = this.toEnglishDigits(value);
        this.checkTimeValidation();
      }
    },

    setOrderPercent({ target: { value } }) {
      if (value && value !== "-") {
        const str = value.toString();
        const res = this.toEnglishDigits(str);
        this.orderPercent = res ? parseInt(res) : "";
        this.checkOrderPercentValidation();

        const floatRate = parseFloat(this.originalRate);
        const floatPercent = parseFloat(res);
        const result = floatRate + (floatPercent * floatRate) / 100;
        const expo = this.$exponent(result, this.baseExponent);
        this.order = result ? this.$thousandMask(expo) : "";
        this.checkOrderValidation();

        this.rate = result ? result : this.rate;
      } else if (value === "") {
        this.order = "";
        this.orderPercentRules = "";
        this.orderRules = "";
        this.resetRate();
      }
      this.updatePrice();
      this.updateSlider();
    },

    setOrder({ target: { value } }) {
      const set = order => {
        this.valueStatus = true;
        const str = order.toString();
        const enDigits = this.toEnglishDigits(str);
        const percent =
          (this.removeThousand(enDigits) * 100) / this.originalRate - 100;

        const pureOrder = this.removeThousand(str);
        const zeroLead = this.leadingZero(pureOrder);
        this.order = this.$thousandMask(zeroLead);
        this.checkOrderValidation();

        /* set orderPercent */
        if (order === "") {
          this.orderPercent = "";
          this.orderRules = "";
          this.orderPercentRules = "";
          this.resetRate();
        } else {
          const expoRemoved = this.$exponent(percent);
          this.orderPercent = percent ? parseInt(expoRemoved) : "";
          this.checkOrderPercentValidation();
          this.rate = this.removeThousand(order);
        }
        this.updatePrice();
        this.updateSlider();
      };

      /* check exponent */
      if (value.includes(".")) {
        const spl = value.split(".");
        const decimal = spl[1];
        if (decimal.length > this.baseExponent) {
          const noComma = this.removeThousand(value);
          const expo = this.$exponent(noComma, this.baseExponent);
          this.order = this.$thousandMask(expo);
        } else {
          set(value);
        }
      } else {
        set(value);
      }
    },

    updateMaxTtl() {
      this.orderHour = this.maxHour.toString();
      this.orderMinute = this.maxMinute.toString();
    },

    submit: debounce(function() {
      this.timeValidation = !this.$refs.form.validate();
      if (this.showOrderLimit) {
        if (this.$refs.form.validate()) {
          const data = {
            base_amount: this.purePrice,
            asset_amount: this.pureAmount,
            market_id: this.market.id,
            direction: this.buySellState === "buy" ? "BUY" : "SELL",
            expires_at: this.orderHour,
            is_from_app: false
          };
          OrderService.inquiry(data)
            .then(res => {
              this.dialog = this.$refs.form.validate();
              this.resultData = res.data.data;
              this.resultData.from_iso =
                this.buySellState === "buy"
                  ? this.base.iso
                  : this.desCurrency.iso;
              this.resultData.to_iso =
                this.buySellState === "buy"
                  ? this.desCurrency.iso
                  : this.base.iso;
              this.resultData.market_id = this.market.id;
              this.resultData.rate = this.order.replace(/\$\s?|(,*)/g, "");
            })
            .catch(error => {
              this.$toast.error(error.data.message);
            })
            .finally(() => (this.loading = false));
        }
      } else {
        const data = {
          base_id:
            this.buySellState === "buy" ? this.baseId : this.desCurrency.id,
          base_amount:
            this.buySellState === "buy" ? this.purePrice : this.pureAmount,
          asset_id:
            this.buySellState === "buy" ? this.desCurrency.id : this.baseId,
          asset_amount:
            this.buySellState === "buy" ? this.pureAmount : this.purePrice
        };
        OrderService.orderToken(data)
          .then(res => {
            this.dialog = this.$refs.form.validate();
            this.resultData = res.data.order_data;
            this.resultData.token = res.data.token;
            this.resultData.from_iso =
              this.buySellState === "buy"
                ? this.base.iso
                : this.desCurrency.iso;
            this.resultData.to_iso =
              this.buySellState === "buy"
                ? this.desCurrency.iso
                : this.base.iso;
            this.resultData.from_id =
              this.buySellState === "buy" ? this.baseId : this.desCurrency.id;
            this.resultData.to_id =
              this.buySellState === "buy" ? this.desCurrency.id : this.baseId;
          })
          .catch(error => {
            this.$toast.error(error.data.message);
          });
      }
    }, 1000),

    toEnglishDigits(value) {
      const persian = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
      return value.toString().replace(/[۰-۹]/g, chr => {
        const enDigits = persian.indexOf(chr);
        return enDigits?.toString().replaceAll("٫", ".");
      });
    },

    emptyInputs() {
      this.amount = "";
      this.price = "";
      this.sliderValue = "0";
      this.order = "";
      this.orderPercent = "";
    },
    resetRangeSlider() {
      this.sliderValue = 0;
    },
    emptyValidations() {
      this.amountRules = "";
      this.priceRules = "";
      this.orderRules = "";
      this.orderPercentRules = "";
      this.timeRules = "";
    },

    toggleNotificationDialog() {
      this.notificationDialog = !this.notificationDialog;
    },

    goToDeposit() {
      // if (this.base.iso === "IRR")
      //   this.$router.push(
      //     "/panel/operations/deposit?from=IRR&paymentType=online"
      //   );
      // else this.$router.push(`/panel/operations/deposit?from=${this.base.iso}`);
      this.$router.push(`/wallet/main`);
    }
  },

  watch: {
    amount(newValue, oldValue) {
      const isPositiveNumbers = str => {
        const reg = new RegExp(/^([0-9,.۰-۹٫])*$/);
        return reg.test(str);
      };

      const withoutDot = newValue.replace(".", "");
      if (withoutDot.includes(".")) {
        this.amount = oldValue;
      } else if (!isPositiveNumbers(newValue)) {
        this.amount = oldValue;
      }
    },

    price(newValue, oldValue) {
      const isPositiveNumbers = str => {
        const reg = new RegExp(/^([0-9,.۰-۹٫])*$/);
        return reg.test(str);
      };

      const withoutDot = newValue.replace(".", "");
      if (withoutDot.includes(".")) {
        this.price = oldValue;
      } else if (!isPositiveNumbers(newValue)) {
        this.price = oldValue;
      }
    },

    base(newBase, oldBase) {
      if (newBase.id !== oldBase.id) {
        const isStill = this.assets.some(
          currency => currency.asset_currency_id === this.desCurrency.id
        );
        if (isStill && !this.order) {
          this.resetRate();
          this.updatePrice();
        } else {
          const firstCurrency = this.currencies.find(
            currency => currency.id === this.assets[0]?.asset_currency_id
          );
          this.setAsset(firstCurrency);
          /* process continues in asset watch */
        }
      }
    },

    market(newMarket, oldMarket) {
      if (newMarket.id !== oldMarket.id) {
        this.emptyValidations();
        this.emptyInputs();
        this.resetRate();
        this.updateMaxTtl();
      } else if (this.order) {
        this.updatePrice();
        this.updateSlider();
      } else {
        this.resetRate();
        this.updatePrice();
        this.updateSlider();
      }
    },

    buySellState(newTab) {
      if (newTab !== this.oldTab) {
        this.priceRules = "";
        this.amountRules = "";
        this.orderRules = "";
        this.orderPercentRules = "";
        this.oldTab = newTab;
      }
      // const buySell = newTab === "sell" ? "buy" : "sell";
      // const pureRate = this.$rate(this.base, this.desCurrency, buySell);
      // const expoRate = this.$exponent(pureRate, this.baseExponent);
      // if (this.pureOrder === expoRate) {
      //   /* order has NOT been changed */
      //   this.resetRate();
      // }
      this.resetRate();
      this.emptyInputs();
    },

    order(newValue, oldValue) {
      const isPositiveNumbers = str => {
        const reg = new RegExp(/^([0-9,.۰-۹٫])*$/);
        return reg.test(str);
      };
      const withoutDot = newValue.replace(".", "");
      if (withoutDot.includes(".")) {
        this.order = oldValue;
      } else if (!isPositiveNumbers(newValue)) {
        this.order = oldValue;
      }
    },

    orderPercent(newValue, oldValue) {
      const isOnlyDigit = str => {
        const reg = new RegExp(/^-?([0-9.۰-۹])*$/);
        return reg.test(str);
      };
      let newValueString = newValue.toString();
      if (newValueString.includes(".")) {
        const withoutDot = newValueString.replace(".", "");
        if (withoutDot.includes(".")) {
          this.orderPercent = oldValue;
        } else {
          const spl = newValueString.split(".");
          const decimals = spl[1];
          if (decimals.length > 2) {
            this.orderPercent = oldValue;
          }
        }
      } else if (newValue !== "-" && !isOnlyDigit(newValue)) {
        this.orderPercent = "";
      } else if (newValue !== "-" && !isOnlyDigit(newValue)) {
        this.orderPercent = "";
      }
    },

    // orderHour(value, oldValue) {
    //   const isOnlyTime = str => {
    //     const reg = new RegExp(/^\d*$/);
    //     return reg.test(str);
    //   };
    //
    //   if (!isOnlyTime(value) || value < 0) {
    //     this.orderHour = oldValue;
    //   }
    // },

    orderMinute(value, oldValue) {
      const isOnlyTime = str => {
        const reg = new RegExp(/^\d*$/);
        return reg.test(str);
      };

      if (!isOnlyTime(value) || value < 0) {
        this.orderMinute = oldValue;
      }
    }
  }
};
